import useTranslation from "next-translate/useTranslation"
import Image from 'next/image'
import { useRouter } from "next/router"
import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { IPublicStatistics } from "@api/schema"
import FilterItem from "@components/common/FilterItem"
import { iconUrl } from "@components/common/Icon"
import { loadStatisticsAction } from "@redux/helper/actions"
import { IStatisticsSingleState } from "@redux/helper/reducers"
import { AppState } from "@redux/reducer"
import { StatisticsType } from "@redux/reduxTypes"
import {
  MarketProjectFilterType, initialMarketSearchParams, setMarketSearchParamsAction,
} from "@redux/usecases/marketplace"
import { useCategories } from "@services/hooks/useCategories"
import { Routes } from "@services/routes"
import { MODULE_EXTENDED_CATEGORIES_AVAILABLE, MODULE_PARTNER_MARKET_AVAILABLE } from "config"


export const TESTID_MARKET_FILTER_ELEMENT = "element-" // extended by component

/** non project filter items of the market tab */
export enum MarketTabItems {
  ExtendedCategories = "extendedCategories",
  ProjectsChosen = "projects",
  SupportRequests = "supportRequests"
}

interface IProps {
  /** what kind of item is active */
  chosenItem: MarketTabItems
}

/**
 * this component provides tabs for filtering projects in the marketplace or for linking to
 * marketplace related pages like the extended category-page
 */
const MarketFilterTabs: React.FC<IProps> = (props: IProps) => {
  const { t } = useTranslation("common")
  const router = useRouter()
  const dispatch = useDispatch()
  const { chosenItem } = props

  const publicStatistics = useSelector((state: AppState) => state.statistics[StatisticsType.Platform] as IStatisticsSingleState<IPublicStatistics>)
  const currentFilterItemState = useSelector((state: AppState) => state.marketplaceSearchState)?.projectTypeFilter
  const { categories } = useCategories()
  const extendedCategoryItemVisible = MODULE_EXTENDED_CATEGORIES_AVAILABLE && categories?.length > 0
  // @todo error handling on request errors


  useEffect(() => {
    if (!publicStatistics?.loaded && !publicStatistics?.isLoading) {
      dispatch(loadStatisticsAction(StatisticsType.Platform))
    }
  }, [])

  const extendedCategoriesChosen = chosenItem === MarketTabItems.ExtendedCategories
  const supportRequestsChosen = chosenItem === MarketTabItems.SupportRequests
  const projectsChosen = chosenItem === MarketTabItems.ProjectsChosen

  // projectFilter-Buttons are active on special situations when the filter is used with MarketTabItems.ProjectsChosen
  const ideaButtonChosen = projectsChosen && currentFilterItemState?.includes(MarketProjectFilterType.Ideas)
  const planningProjectsButtonChosen = projectsChosen && currentFilterItemState?.includes(MarketProjectFilterType.ProjectsInPlanning)
  const runningProjectsButtonChosen = projectsChosen && currentFilterItemState?.includes(MarketProjectFilterType.ProjectsRunning)
  const finishedProjectsButtonChosen = projectsChosen && currentFilterItemState?.includes(MarketProjectFilterType.ProjectsFinished)

  /** function to be called when the user clicks one of the filter items */
  const onFilterItemChanged = (newProjectTypeFilter: MarketProjectFilterType) => {
    dispatch(setMarketSearchParamsAction(
      {
        ...initialMarketSearchParams,
        projectTypeFilter: [newProjectTypeFilter]
      }
    ))
    void router.push(Routes.Marketplace)
  }


  // type of individual form filter buttons
  interface FormFilterButton {
    textKey: string
    iconURL: string
    filterType: MarketProjectFilterType
    isChosen: boolean
    classNameExtra?: string
    isVisible: boolean
  }

  // list of 4 form filter buttons to be mapped to a FilterItem
  const formFilterButtons: FormFilterButton[] = [
    {
      textKey: "ideas",
      iconURL: iconUrl("light-bulb"),
      filterType: MarketProjectFilterType.Ideas,
      isChosen: ideaButtonChosen,
      isVisible: true,
    },
    {
      textKey: "planning",
      iconURL: iconUrl("project"),
      filterType: MarketProjectFilterType.ProjectsInPlanning,
      isChosen: planningProjectsButtonChosen,
      classNameExtra: "grouped-filteritem",
      isVisible: true,
    },
    {
      textKey: "running",
      iconURL: iconUrl("project-implementation"),
      filterType: MarketProjectFilterType.ProjectsRunning,
      isChosen: runningProjectsButtonChosen,
      classNameExtra: "grouped-filteritem",
      isVisible: publicStatistics?.data?.runningProjects > 0,
    },
    {
      textKey: "finished",
      iconURL: iconUrl("check-in-circle"),
      filterType: MarketProjectFilterType.ProjectsFinished,
      isChosen: finishedProjectsButtonChosen,
      classNameExtra: "grouped-filteritem",
      isVisible: publicStatistics?.data?.finishedProjects > 0,
    }
  ]

  return <div className="icon-filteritem-container pb-4">
    {extendedCategoryItemVisible === true &&
      // this does not have to be a FilterItem, a Link would also be okay; but it's easy...
      <FilterItem
        isActive={extendedCategoriesChosen}
        onClick={() => {
          void router.push(Routes.Categories)
        }}
        tooltipText={t("marketplace.filterIcons.categories")}
        isImage
        disabled={extendedCategoriesChosen}
        data-testid={TESTID_MARKET_FILTER_ELEMENT + "categories"}
      >
        <Image className="icon-filteritem"
          src={iconUrl("flash-light")}
          alt={t("marketplace.filterIcons.categories")}
          layout="intrinsic"
          width={90}
          height={90}
          priority
        />
      </FilterItem>
    }
    {publicStatistics?.data?.activeProjects > 0 &&
      formFilterButtons.map((button: FormFilterButton) => button.isVisible &&
        <FilterItem key={button.textKey}
          isActive={button.isChosen}
          // determine whether the current button is the only one that is chosen
          disabled={button.isChosen && (1 === formFilterButtons.filter((b: FormFilterButton) => b.isChosen).length)}
          onClick={() => onFilterItemChanged(button.filterType)}
          tooltipText={t("marketplace.filterIcons." + button.textKey)}
          isImage
          additionalClassName={button.classNameExtra}
        >
          <Image className="icon-filteritem"
            src={button.iconURL}
            alt={t("marketplace.filterIcons." + button.textKey)}
            layout="intrinsic"
            width={90}
            height={90}
            priority
          />
        </FilterItem>)
    }
    {MODULE_PARTNER_MARKET_AVAILABLE &&
      // this does not have to be a FilterItem, a Link would also be okay; but it's easy...
      <FilterItem
        isActive={supportRequestsChosen}
        onClick={() => {
          void router.push(Routes.PartnerMarket)
        }}
        tooltipText={t("marketplace.filterIcons.partnermarket")}
        isImage
        disabled={supportRequestsChosen}
        data-testid={TESTID_MARKET_FILTER_ELEMENT + "partnermarket"}
      >
        <Image className="icon-filteritem"
          src={iconUrl("partner")}
          alt={t("marketplace.filterIcons.partnermarket")}
          layout="intrinsic"
          width={90}
          height={90}
          priority
        />
      </FilterItem>
    }
  </div>
}

export default MarketFilterTabs
